// @djsem/shared — validation core.
//
// CHOSEN SCHEMA TOOLING: a dependency-free combinator library. Rationale: no schema
// package (zod/valibot/etc.) exists anywhere in the workspace today, and adding one is
// a licensing/supply-chain decision we don't take unilaterally (hard rule 10). These
// combinators give every contract a runtime validator with precise issue paths, zero
// dependencies, and trivially replaceable internals if the team later standardizes on
// a library — the public shape (Validator<T>, ValidationResult) would not change.
//
// Validators are PURE (allowed by rule 12 — schema validation is stateless and clearer
// as functions than classes).

/** A single validation failure at a specific path. */
export interface ValidationIssue { path: string; message: string }
/** Boolean pass/fail validation outcome with per-path issues. */
export interface ValidationResult { valid: boolean; issues: ValidationIssue[] }
/** A pure validation function: given a value (and optional path prefix for nested calls), returns a {@link ValidationResult}. */
export type Validator<T = unknown> = (value: unknown, path?: string) => ValidationResult;

// ---- Prompt 03 canonical contract shapes ------------------------------------
// These are the DJSEM-prefixed shapes named in the shared-contracts spec. Rather
// than fork the tested `ValidationResult` engine above (which everything already
// consumes via `.valid`), the richer shape is provided as an ADAPTER over it — one
// model, two views, no duplication. `severity` is always "error" here because the
// combinator core is boolean pass/fail; richer severities are set by hand-written
// domain validators that need them.
/** A single validation issue in the richer DJSEM-prefixed shape (adds a code and severity). */
export interface DJSEMValidationIssue {
  path: string;
  code: string;
  message: string;
  severity: "info" | "warning" | "error" | "critical";
}
/** The DJSEM-prefixed validation result shape used across canonical contracts — an adapter over {@link ValidationResult}. */
export interface DJSEMValidationResult {
  ok: boolean;
  issues: DJSEMValidationIssue[];
}
/**
 * Adapt a core {@link ValidationResult} into the richer {@link DJSEMValidationResult} shape.
 * @param r - The core validation result to adapt.
 * @param code - The issue code to attach to every resulting issue.
 * @returns The adapted result (`severity` is always "error", since the combinator core is boolean pass/fail).
 */
export function toDJSEMValidationResult(r: ValidationResult, code = "invalid"): DJSEMValidationResult {
  return {
    ok: r.valid,
    issues: r.issues.map((i) => ({ path: i.path, code, message: i.message, severity: "error" as const })),
  };
}

// Result/error envelopes for API and IPC boundaries (Cloudflare Worker ⇄ web,
// Electron main ⇄ renderer). Genuinely absent before Prompt 03; added here.
/** A failed API/IPC response envelope. */
export interface DJSEMApiErrorEnvelope {
  ok: false;
  error: { code: string; message: string; details?: unknown };
}
/** A successful API/IPC response envelope carrying its payload. */
export interface DJSEMApiSuccessEnvelope<T> {
  ok: true;
  data: T;
}
/** A discriminated-union API/IPC response envelope (Worker ⇄ web, Electron main ⇄ renderer). */
export type DJSEMApiEnvelope<T> = DJSEMApiSuccessEnvelope<T> | DJSEMApiErrorEnvelope;
/**
 * Build a successful API envelope.
 * @param data - The success payload.
 * @returns The success envelope.
 */
export const apiOk = <T>(data: T): DJSEMApiSuccessEnvelope<T> => ({ ok: true, data });
/**
 * Build a failed API envelope.
 * @param code - Machine-readable error code.
 * @param message - Human-readable error message.
 * @param details - Optional extra error detail.
 * @returns The error envelope.
 */
export const apiErr = (code: string, message: string, details?: unknown): DJSEMApiErrorEnvelope =>
  ({ ok: false, error: { code, message, ...(details === undefined ? {} : { details }) } });

const ok = (): ValidationResult => ({ valid: true, issues: [] });
const fail = (path: string, message: string): ValidationResult => ({ valid: false, issues: [{ path, message }] });
const merge = (...rs: ValidationResult[]): ValidationResult => {
  const issues = rs.flatMap((r) => r.issues);
  return { valid: issues.length === 0, issues };
};

// ---- primitives -------------------------------------------------------------

/**
 * Build a validator for strings, optionally constrained by length, pattern, or enum.
 * @param opts - Optional min/max length, regex pattern, and/or allowed enum values.
 * @returns A {@link Validator} for strings matching the given constraints.
 */
export const vString = (opts: { min?: number; max?: number; pattern?: RegExp; enum?: readonly string[] } = {}): Validator<string> =>
  (v, path = "$") => {
    if (typeof v !== "string") return fail(path, "expected string");
    if (opts.min !== undefined && v.length < opts.min) return fail(path, `string shorter than ${opts.min}`);
    if (opts.max !== undefined && v.length > opts.max) return fail(path, `string longer than ${opts.max}`);
    if (opts.pattern && !opts.pattern.test(v)) return fail(path, `string does not match ${opts.pattern}`);
    if (opts.enum && !opts.enum.includes(v)) return fail(path, `expected one of [${opts.enum.join(", ")}]`);
    return ok();
  };

/**
 * Build a validator for finite numbers, optionally constrained by range or integer-ness.
 * @param opts - Optional min/max bounds and whether the value must be an integer.
 * @returns A {@link Validator} for numbers matching the given constraints.
 */
export const vNumber = (opts: { min?: number; max?: number; int?: boolean } = {}): Validator<number> =>
  (v, path = "$") => {
    if (typeof v !== "number" || !Number.isFinite(v)) return fail(path, "expected finite number");
    if (opts.int && !Number.isInteger(v)) return fail(path, "expected integer");
    if (opts.min !== undefined && v < opts.min) return fail(path, `number below ${opts.min}`);
    if (opts.max !== undefined && v > opts.max) return fail(path, `number above ${opts.max}`);
    return ok();
  };

/** Validator that accepts only booleans. */
export const vBoolean: Validator<boolean> = (v, path = "$") =>
  typeof v === "boolean" ? ok() : fail(path, "expected boolean");

/**
 * Build a validator that accepts only one exact literal value.
 * @param lit - The literal value the input must equal.
 * @returns A {@link Validator} for that literal.
 */
export const vLiteral = <T extends string | number | boolean>(lit: T): Validator<T> =>
  (v, path = "$") => (v === lit ? ok() : fail(path, `expected literal ${JSON.stringify(lit)}`));

/** Validator for 0..1 unit values (confidence/intensity, used throughout DJSEM contracts). */
export const vUnit = (): Validator<number> => vNumber({ min: 0, max: 1 });

/** Validator for ISO-8601 date strings. */
export const vIsoDate = (): Validator<string> =>
  (v, path = "$") => {
    if (typeof v !== "string" || Number.isNaN(Date.parse(v))) return fail(path, "expected ISO-8601 date string");
    return ok();
  };

/** Validator for id strings (non-empty, capped at 200 chars). */
export const vId = (): Validator<string> => vString({ min: 1, max: 200 });

// ---- containers -------------------------------------------------------------

/**
 * Build a validator for arrays whose items each pass a given validator.
 * @param item - The validator applied to every array element.
 * @param opts - Optional min/max array length.
 * @returns A {@link Validator} for arrays of `T`.
 */
export const vArray = <T>(item: Validator<T>, opts: { min?: number; max?: number } = {}): Validator<T[]> =>
  (v, path = "$") => {
    if (!Array.isArray(v)) return fail(path, "expected array");
    if (opts.min !== undefined && v.length < opts.min) return fail(path, `array shorter than ${opts.min}`);
    if (opts.max !== undefined && v.length > opts.max) return fail(path, `array longer than ${opts.max}`);
    return merge(...v.map((x, i) => item(x, `${path}[${i}]`)));
  };

/** A map of field name to the validator that field must satisfy. */
export interface ObjectSpec { [field: string]: Validator }
/**
 * Build a validator for plain objects with required and optional fields. By default
 * unknown fields are rejected; pass `opts.open: true` to allow them.
 * @param required - Fields that must be present and valid.
 * @param optional - Fields that, if present, must be valid.
 * @param opts - Set `open: true` to allow fields not listed in `required`/`optional`.
 * @returns A {@link Validator} for the described object shape.
 */
export const vObject = (required: ObjectSpec, optional: ObjectSpec = {}, opts: { open?: boolean } = {}): Validator<Record<string, unknown>> =>
  (v, path = "$") => {
    if (typeof v !== "object" || v === null || Array.isArray(v)) return fail(path, "expected object");
    const o = v as Record<string, unknown>;
    const results: ValidationResult[] = [];
    for (const [k, val] of Object.entries(required)) {
      results.push(k in o ? val(o[k], `${path}.${k}`) : fail(`${path}.${k}`, "missing required field"));
    }
    for (const [k, val] of Object.entries(optional)) {
      if (k in o && o[k] !== undefined) results.push(val(o[k], `${path}.${k}`));
    }
    if (!opts.open) {
      const known = new Set([...Object.keys(required), ...Object.keys(optional)]);
      for (const k of Object.keys(o)) if (!known.has(k)) results.push(fail(`${path}.${k}`, "unknown field"));
    }
    return merge(...results);
  };

/**
 * Build a validator for a record (dictionary) whose values each pass a given validator.
 * @param value - The validator applied to every value in the record.
 * @returns A {@link Validator} for `Record<string, T>`.
 */
export const vRecord = <T>(value: Validator<T>): Validator<Record<string, T>> =>
  (v, path = "$") => {
    if (typeof v !== "object" || v === null || Array.isArray(v)) return fail(path, "expected record object");
    return merge(...Object.entries(v as Record<string, unknown>).map(([k, x]) => value(x, `${path}.${k}`)));
  };

/**
 * Build a validator that passes if any one of several validators passes.
 * @param members - The candidate validators; the value must satisfy at least one.
 * @returns A {@link Validator} that succeeds if any member validator succeeds.
 */
export const vUnion = (...members: Validator[]): Validator =>
  (v, path = "$") => {
    const attempts = members.map((m) => m(v, path));
    if (attempts.some((a) => a.valid)) return ok();
    return { valid: false, issues: [{ path, message: `no union member matched (${attempts.flatMap((a) => a.issues.map((i) => i.message)).join(" | ")})` }] };
  };

/**
 * Wrap a validator to also accept `null` (in addition to whatever it already accepts).
 * @param inner - The validator to wrap.
 * @returns A {@link Validator} that also accepts `null`.
 */
export const vOptionalNull = <T>(inner: Validator<T>): Validator<T | null> =>
  (v, path = "$") => (v === null ? ok() : inner(v, path));

/**
 * Attach an extra cross-field rule to an object validator, run only if the base validator passes.
 * @param inner - The base validator to run first.
 * @param rule - A function returning an error message (or null if valid) given the parsed value.
 * @returns A {@link Validator} that applies both the base validation and the refinement rule.
 */
export const vRefine = <T>(inner: Validator<T>, rule: (value: T) => string | null): Validator<T> =>
  (v, path = "$") => {
    const base = inner(v, path);
    if (!base.valid) return base;
    const msg = rule(v as T);
    return msg ? fail(path, msg) : ok();
  };

/**
 * Build a validator that scans every string (and key) in a structure for forbidden
 * vocabulary. Used to enforce renderer/hardware-independence (rule 7) at the contract
 * level, not just document it.
 * @param pattern - The forbidden-term pattern to scan for.
 * @param label - A label included in issue messages to identify which rule matched.
 * @returns A {@link Validator} that flags every match found anywhere in the structure.
 */
export const vForbiddenTerms = (pattern: RegExp, label: string): Validator =>
  (v, path = "$") => {
    const issues: ValidationIssue[] = [];
    const walk = (x: unknown, p: string) => {
      if (typeof x === "string" && pattern.test(x)) issues.push({ path: p, message: `${label}: forbidden term in "${x}"` });
      else if (Array.isArray(x)) x.forEach((e, i) => walk(e, `${p}[${i}]`));
      else if (typeof x === "object" && x !== null) Object.entries(x).forEach(([k, e]) => {
        if (pattern.test(k)) issues.push({ path: `${p}.${k}`, message: `${label}: forbidden term in key "${k}"` });
        walk(e, `${p}.${k}`);
      });
    };
    walk(v, path);
    return { valid: issues.length === 0, issues };
  };
